import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  InputAdornment,
  TextField,
  Divider,
  Stack,
} from "@mui/material";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import CheckoutForm from "../components/CheckoutForm";
import CustomButton from "../components/CustomButton";
import { debounce } from 'lodash';

import Logo from "../static/Images/logo.png"


const API_URL = process.env.REACT_APP_API_URL


const RequestSong = () => {
  const navigate = useNavigate()
  const [song, setSong] = useState("");
  const [artist, setArtist] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState("");

  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntent, setPaymentIntent] = useState("");
  const [currentEvent, setCurrentEvent] = useState();
  const [paymentsAllowed, setPaymentsAllowed] = useState();
  const suggestionsRef = useRef();
  const { eventId } = useParams();


  //Search State
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/stripe/config`).then(async (r) => {
      const { publishableKey } = await r.json();
      setStripePromise(loadStripe(publishableKey));
    });
  }, []);

  useEffect(() => {
    const getEvent = async () => {
      await fetch(`${API_URL}/firebase/get-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventId: eventId,
        })
      }).then((res) => res.json())
      .then((res) => {
        setCurrentEvent(res)
        setPaymentsAllowed(res.PaymentsAllowed)
        if(res.PaymentsAllowed) {
          setAmount(res.MinAmount)
          setSelectedAmount(res.MinAmount)
        } else {
          setAmount(0)
          setSelectedAmount(0)
        }
      })
    }
    getEvent();
  }, [eventId])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  

  const handleSubmit = async (e) => {
    if (paymentsAllowed && parseFloat(amount) < parseFloat(currentEvent.MinAmount)) {
      alert(`The minimum bid ammount for this event is ${currentEvent.MinAmount}`)
    } else {
      setSelectedAmount(amount)
      await fetch(`${API_URL}/stripe/create-payment-intent`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({
          chargeAmount: amount,
        }),
      }).then((res) => res)
      .then(async (res) => {
        const { clientSecret, paymentIntentId } = await res.json();
        if (clientSecret !== "-1" && paymentIntentId !== "-1" && paymentsAllowed){
          setClientSecret(clientSecret);
          setPaymentIntent(paymentIntentId);
        } else {
          fetch(`${API_URL}/firebase/create-bid`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              eventId: eventId,
              song: song,
              artist: artist,
              amount: amount,
              paymentIntentId: paymentIntentId,
              acceptedBid: true
            })
          })
          navigate(`/Events/${eventId}`)
        }
      });
    }
  };

  const debouncedSearch = debounce(async (value) => {
    if (value) {
      await fetch(`${API_URL}/spotify/get-song-match?q=${value}`)
      .then((res) => res.json())
      .then((res) => {
        setSuggestions(res.songs);
      });
    } else {
      setSuggestions([]);
    }
  }, 100); // waits 300ms after the last keystroke

  const updateInputValue = (value) => {
    if(value.length >= 3){
      setInputValue(value);
      debouncedSearch(value);
    } else {
      setInputValue(value);
      debouncedSearch("");
    }
  }

  const setSelectedSong = (songName, artistName) => {
    setSong(songName)
    setArtist(artistName)
    setSuggestions([])
  }

  return (
    <Box className="max_container" sx = {{ marginTop: 8 }}>  
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        className="relative px-2 sm:px-0 py-2 -mt-10">
          <Box  className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
          <a href="/">
            <img
              src={Logo}
              alt="thaml"
              width={75}
              height={32}
              className="cursor-pointer"
            />
          </a>
        </Box>
      </Stack>
      {(song && artist) ? (
        <Box className="px-1"sx = {{ marginTop: 8 }}>
        <TextField
          id="input-with-icon-textfield"
          label="Selected Song"
          placeholder="input"
          fullWidth
          value={song + " by " + artist}
          onChange={e => {
            setArtist("")
            setSong("")
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DriveFileRenameOutlineOutlinedIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        >
          {inputValue === '' && <span className="subvalue">{artist}</span>}
        </TextField>
      </Box>
      ) :
      (<Box className="px-1"sx = {{ marginTop: 8 }}>
        <TextField
          id="input-with-icon-textfield"
          label="Search Song"
          placeholder="input"
          fullWidth
          value={inputValue}
          onChange={e => {
            updateInputValue(e.target.value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DriveFileRenameOutlineOutlinedIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        >
          {inputValue === '' && <span className="subvalue">{artist}</span>}
        </TextField>
      </Box>)
      }
      <div ref={suggestionsRef}>
        { suggestions && suggestions.map((suggestion, index) => (
          <Box className="px-1" sx={{ marginTop: 1 }}>
          <button 
            onClick={() => setSelectedSong(
              suggestion.songName,
              suggestion.artistName
            )}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '5px',
              padding: '18.5px 14px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <p style={{margin: '0', textAlign: 'center', color: 'white', fontWeight: '600'}}>{suggestion.songName}</p>
            <p style={{margin: '0', textAlign: 'center', color: 'white', fontWeight: '300'}}>{suggestion.artistName}</p>
          </button>
        </Box>
        ))}

        </div>
      <Divider sx={{ marginTop: 4 }}/>
      {
        paymentsAllowed && <>
          <Box className="px-1">
            <TextField
              id="input-with-icon-textfield"
              label="Tip Amount"
              type="number"
              placeholder="input"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyOutlinedIcon />
                  </InputAdornment>
                ),clientSecret
                
              }}
              variant="outlined"
            />
          </Box>
          <Divider sx={{ marginTop: 1 }}/>
          <p>The payment will be canceled if the request is not accepted</p>
        </>
      }
      <Divider sx={{ marginTop: 4 }}/>
      {clientSecret && stripePromise ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm
            paymentIntentId={paymentIntent}
            song={song}
            artist={artist}
            amount={selectedAmount}
          />
        </Elements>
      ) : (
        <CustomButton 
          myFunction={handleSubmit}
          text={"Submit Song"}
        />
      )}
       
    </Box>
  );
};

export default RequestSong;
